import { useEffect, useRef } from 'react'
import { UseFormSetValue, FieldValues } from 'react-hook-form'
import { getFieldsToClearOnHide, getFieldsToClearOnShow } from '../components/ListView/filterConfig'

/**
 * Custom hook to clear filter values when their corresponding columns are hidden.
 *
 * @param type - The filter type (e.g., 'origin', 'itemCollection', 'items', etc.)
 * @param columnVisibility - Current column visibility state
 * @param setValue - React Hook Form's setValue function (for visual clearing)
 * @param clearSpecificFields - From useFilterSync: clears fields and submits directly
 * @param onClear - Optional callback for extra cleanup (e.g. resetting input state)
 */
export function useClearFiltersOnColumnHide(
  type: string | undefined,
  columnVisibility: { [key: string]: boolean } | undefined,
  setValue: UseFormSetValue<FieldValues>,
  clearSpecificFields: (fieldNames: string[]) => void,
  onClear?: () => void,
) {
  const prevColumnVisibilityRef = useRef<{ [key: string]: boolean }>({})
  const clearSpecificFieldsRef = useRef(clearSpecificFields)
  clearSpecificFieldsRef.current = clearSpecificFields
  const onClearRef = useRef(onClear)
  onClearRef.current = onClear

  useEffect(() => {
    if (!type || !columnVisibility) return

    const prevVisibility = prevColumnVisibilityRef.current
    const fieldsToClear = getFieldsToClearOnHide(
      type,
      columnVisibility,
      prevVisibility,
    )

    if (fieldsToClear.length > 0) {
      // Clear visually
      fieldsToClear.forEach((fieldName) => {
        setValue(fieldName, null)
      })
      onClearRef.current?.()
      // Submit directly — same mechanism as clearFilters, no watch/timing dependency
      clearSpecificFieldsRef.current(fieldsToClear)
    }

    // When a column transitions from hidden→visible, RHF may restore a stale field
    // value (shouldUnregister: false retains values for unmounted fields). Reset them.
    const fieldsToResetOnShow = getFieldsToClearOnShow(
      type,
      columnVisibility,
      prevVisibility,
    )
    fieldsToResetOnShow.forEach((fieldName) => {
      setValue(fieldName, null)
    })

    prevColumnVisibilityRef.current = { ...columnVisibility }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnVisibility, setValue, type])
}
